/*
 *  Copyright (C) 2020 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import { fromNullable, fromPredicate, none, some } from 'fp-ts/lib/Option';
import { SyntheticLayerInfo } from 'sdi/app';
import { nameToString } from 'sdi/components/button/names';
import { DIV, SPAN } from 'sdi/components/elements';
import { exportLinks } from 'sdi/components/export';
import { Filter, table, TableState } from 'sdi/components/table2';
import { scopeOption } from 'sdi/lib';
import tr, { fromRecord } from 'sdi/locale';
import {
    eqRequestFilters,
    streamFieldName,
    StreamingRequestFilter,
} from 'sdi/source';
import {
    clearSelectedFeatures,
    deselectTableView,
    reloadLayer,
    resetDataFilters,
    setDataFilters,
    setLayout,
} from '../../events/app';
import {
    clearCurrentStreamData,
    selectFeatureFromRow,
    tableDispatch,
} from '../../events/table';
import {
    getAllDataFilters,
    getCurrentLayerInfo,
    getCurrentName,
    getDataFilters,
    getSyntheticLayerInfo,
} from '../../queries/app';
import {
    getExportLink,
    getLayerSource,
    streamLoadingStatus,
    tableQuery,
} from '../../queries/table';
import { makeIcon, makeLabelAndIcon } from '../button';
import { buttonGuard } from 'sdi/components/button';

const logger = debug('sdi:table/feature-collection');

const closeButton = makeIcon('close', 3, 'times', {
    position: 'right',
    text: () => tr.core('close'),
});

const renderStreamingIndicator = () => {
    const s = streamLoadingStatus();
    const inner = (() => {
        switch (s) {
            case 'error':
                return some(SPAN('fa', nameToString('exclamation-triangle')));
            case 'loading':
                return some(
                    SPAN(
                        {
                            className: 'fa fa-spin',
                            title: tr.core('loadingData'),
                        },
                        nameToString('spinner'),
                    ),
                );
            case 'no-stream':
                return none;
            case 'loaded':
                return some(SPAN('fa', nameToString('check')));
        }
    })();
    return SPAN('stream-status', inner);
};

const toolbar = () => {
    const resetMapFiltersButton = makeLabelAndIcon('reset', 3, 'times', () =>
        tr.view('resetMapFilters'),
    );

    const applyFilterToMapButton = makeLabelAndIcon('filter', 3, 'filter', () =>
        tr.view('applyFilterToMap'),
    );

    // an empty pattern matches everything, the table ignores it
    const isEffective = (f: Filter) =>
        f.tag === 'string' ? f.pattern.trim().length > 0 : true;

    const toRequestFilters =
        (fieldNames: string[]) =>
        (filters: Filter[]): StreamingRequestFilter[] =>
            filters.filter(isEffective).map(f => ({
                ...f,
                columnName: fieldNames[f.column],
            }));

    const setMapFilter = (li: SyntheticLayerInfo, filters: Filter[]) => {
        const fieldNames = getLayerSource().fields.map(streamFieldName);
        setDataFilters(li.info.id, toRequestFilters(fieldNames)(filters));
        reloadLayer(li);
    };

    // table filter already reflected on the map
    const isApplied = (li: SyntheticLayerInfo, filters: Filter[]) => {
        const fieldNames = getLayerSource().fields.map(streamFieldName);
        const mapFilters = toRequestFilters(fieldNames)(filters);
        return getDataFilters(li.info.id).fold(false, tableFilters =>
            eqRequestFilters.equals(mapFilters, tableFilters),
        );
    };

    const resetMapFilters = () => {
        const layerIds = Object.keys(getAllDataFilters());
        resetDataFilters();
        layerIds.forEach(layerId =>
            getSyntheticLayerInfo(layerId).map(reloadLayer),
        );
    };

    const tableFilters = scopeOption()
        .let(
            'filters',
            fromPredicate<TableState>(ts => ts.filters.length > 0)(
                tableQuery(),
            ).map(ts => ts.filters),
        )
        .let('li', getCurrentLayerInfo);

    const mapFilters = Object.keys(getAllDataFilters()).length > 0;

    const filterActions = DIV(
        'table-map-filters',
        mapFilters ? some(resetMapFiltersButton(resetMapFilters)) : none,
        tableFilters.map(({ li, filters }) =>
            buttonGuard(!isApplied(li, filters), applyFilterToMapButton, () =>
                setMapFilter(li, filters),
            ),
        ),
    );

    const exportActions = getCurrentLayerInfo().chain(
        ({ info, metadata, name }) =>
            fromNullable(info.exportable)
                .filter(Boolean)
                .map(() =>
                    exportLinks(
                        getExportLink(info, metadata),
                        fromRecord(name),
                    ),
                ),
    );

    const renderRowCount = () =>
        DIV(
            'table-row-count',
            SPAN('', `${tr.core('tableCountLabel')} : `),
            getLayerSource().data(tableQuery().window).filteredCount,
        );

    const close = () => {
        clearSelectedFeatures();
        resetMapFilters();
        deselectTableView();
        setLayout('map-fs');
    };

    return DIV(
        { className: 'table-toolbar', key: 'table-toolbar' },
        DIV('table-title', getCurrentName(), renderStreamingIndicator()),
        DIV(
            'table-tools',
            filterActions,
            exportActions,
            renderRowCount(),
            closeButton(close),
        ),
    );
};

const renderTable = table(
    tableDispatch,
    tableQuery,
    selectFeatureFromRow,
    clearCurrentStreamData,
);

export default () => renderTable(getLayerSource(), toolbar());

logger('loaded');
